import React, { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiX,
  FiCopy,
  FiCheck,
  FiDownload,
  FiExternalLink,
  FiCode,
  FiMaximize2,
} from "react-icons/fi";
import "./CodeViewerModal.css";

const CodeViewerModal = ({ 
  isOpen, 
  onClose, 
  code, 
  language = "javascript", 
  title = "Code Viewer",
  repository = null,
}) => {
  const [copied, setCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  if (!isOpen) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
// Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    const extensions = {
      javascript: "js",
      python: "py",
      java: "java",
      cpp: "cpp",
      csharp: "cs",
      go: "go",
      rust: "rs",
      r: "r",
      matlab: "m",
      sql: "sql",
      bash: "sh",
      typescript: "ts",
      html: "html",
      css: "css",
      json: "json",
      other: "txt",
    };
    const ext = extensions[language] || "txt";
    const filename = `code.${ext}`;
    
    const blob = new Blob([code], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Split code into lines for line numbers
  const lines = code?.split("\n") || [];
  const lineCount = lines.length;

  // Use portal to render at document body level
  return createPortal(
    <AnimatePresence>
      <motion.div
        className="code-viewer-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className={`code-viewer-modal ${isFullscreen ? "code-viewer-modal--fullscreen" : ""}`}
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <header className="code-viewer-header">
            <div className="code-viewer-title-section">
              <div className="code-viewer-icon">
                <FiCode size={20} />
              </div>
              <div>
                <h2 className="code-viewer-title">{title}</h2>
                <div className="code-viewer-meta">
                  <span className="code-viewer-lang">{language}</span>
                  <span className="code-viewer-lines">{lineCount} lines</span>
                </div>
              </div>
            </div>
            
            <div className="code-viewer-actions">
              {repository && (
                <a
                  href={repository}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="code-viewer-btn code-viewer-btn--secondary"
                >
                  <FiExternalLink size={16} />
                  <span>Repository</span>
                </a>
              )}
              <button
                className="code-viewer-btn code-viewer-btn--secondary"
                onClick={() => setIsFullscreen(!isFullscreen)}
                title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              >
                <FiMaximize2 size={16} />
              </button>
              <button
                className="code-viewer-btn code-viewer-btn--secondary"
                onClick={handleDownload}
                title="Download code"
              >
                <FiDownload size={16} />
              </button>
              <button
                className={`code-viewer-btn ${copied ? "code-viewer-btn--success" : "code-viewer-btn--primary"}`}
                onClick={handleCopy}
              >
                {copied ? <FiCheck size={16} /> : <FiCopy size={16} />}
                <span>{copied ? "Copied!" : "Copy"}</span>
              </button>
              <button className="code-viewer-close" onClick={onClose}>
                <FiX size={20} />
              </button>
            </div>
          </header>

          {/* Code Content */}
          <div className="code-viewer-content">
            <div className="code-viewer-code-wrapper">
              <div className="code-viewer-line-numbers">
                {lines.map((_, idx) => (
                  <span key={idx}>{idx + 1}</span>
                ))}
              </div>
              <pre className="code-viewer-code">
                <code>{code}</code>
              </pre>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default CodeViewerModal;
